import { style } from '@vanilla-extract/css';
import { vars } from '@/shared/ui/theme/contract.css';

/**
 * 발송 확인 (퀘스트 7ca8908c) — 섭외 IA §3-5.
 *
 * 되돌릴 수 없는 마지막 관문이라 **나갈 통 수 하나만 굵다**. 나머지 여섯 줄은 ✓/⚠ 로 훑고,
 * ⚠ 줄만 멈춰 읽게 한다(승인 시안 ConfirmAfter 2026-09-24).
 */
/** 머리 · 요약 한 문장 · 확인 줄 · 발송 방식 · 같은 주소 · 발송 줄이 세로로 선다. */
export const stack = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.md,
});

export const card = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.md,
  padding: vars.spacing.padding.md,
  borderRadius: vars.spacing.borderRadius.lg,
  background: vars.surface.card.default,
  border: `1px solid ${vars.surface.border.subtle}`,
});

export const heading = style({
  margin: 0,
  fontSize: vars.typography.fontSize.sm,
  fontWeight: String(vars.typography.fontWeight.semibold),
  color: vars.typography.textColor.primary,
});

export const strong = style({
  color: vars.typography.textColor.secondary,
});

/**
 * 상자를 안 두르는 주석 — 같은 주소 카드의 꼬리줄이 이것이다.
 *
 * 그 줄은 바로 위 항목 상자들에 딸린 설명이라, 한 겹 더 상자를 두르면 상자 안의 상자가 되어
 * 어느 것에 붙는 말인지가 흐려진다.
 */
export const plainNote = style({
  margin: 0,
  fontSize: vars.typography.fontSize['2xs'],
  lineHeight: String(vars.typography.lineHeight.relaxed),
  color: vars.typography.textColor.tertiary,
});

export const sharedList = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

export const sharedRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.md,
  padding: vars.spacing.padding.sm,
  borderRadius: vars.spacing.borderRadius.md,
  background: vars.surface.card.hover,
});

export const sharedBody = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
  flexGrow: 1,
});

export const sharedNames = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.primary,
});

export const sharedNote = style({
  margin: 0,
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.typography.textColor.tertiary,
});

/**
 * 마지막 줄 — 버튼 줄 위, 안내 문장 아래(2026-09-30).
 *
 * 전에는 셋이 한 줄이라 안내 문장이 두 버튼 **사이에** 끼었다. 문장이 길어지면 두 버튼의 거리가
 * 그 길이에 끌려다녀 레이아웃이 흔들렸고, 문장이 어느 버튼에 딸린 말인지도 흐렸다.
 */
export const footer = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
  paddingTop: vars.spacing.padding.xs,
  borderTop: `1px solid ${vars.surface.border.subtle}`,
});

/** 테스트와 발송 — 서로 다른 일이라 양 끝으로 벌린다. */
export const footerActions = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: vars.spacing.gap.sm,
});

export const summary = style({
  margin: 0,
  fontSize: vars.typography.fontSize.md,
  lineHeight: String(vars.typography.lineHeight.relaxed),
  color: vars.typography.textColor.primary,
});

export const summaryStrong = style({
  color: vars.bright.status.info,
  fontVariantNumeric: 'tabular-nums',
});

export const checklist = style({
  display: 'flex',
  flexDirection: 'column',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  borderRadius: vars.spacing.borderRadius.lg,
  border: `1px solid ${vars.surface.border.subtle}`,
});

/** 시안의 네 칸 — 표식 · 항목 · 값 · 고치기. 값 칸만 늘어난다. */
export const checkRow = style({
  display: 'grid',
  gridTemplateColumns: '20px 76px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  minHeight: '46px',
  padding: `${vars.spacing.padding.xs} ${vars.spacing.padding.sm}`,
  selectors: {
    '&:not(:last-child)': { borderBottom: `1px solid ${vars.surface.border.subtle}` },
  },
});

export const okMark = style({ display: 'inline-flex', color: vars.bright.status.success });
export const warnMark = style({ display: 'inline-flex', color: vars.bright.status.warning });

export const checkTerm = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const checkBody = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

export const checkValue = style({
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.primary,
  overflowWrap: 'anywhere',
});

export const checkSub = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const checkAction = style({ justifySelf: 'end' });

export const editLink = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.status.info,
});

export const modes = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: vars.spacing.gap.sm,
});

const modeBase = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px',
  minHeight: '52px',
  padding: `${vars.spacing.padding.xs} ${vars.spacing.padding.sm}`,
  borderRadius: vars.spacing.borderRadius.lg,
  fontFamily: 'inherit',
  fontSize: vars.typography.fontSize.sm,
  textAlign: 'left',
});

/** 고른 쪽 — 강조 테두리. */
export const modeOn = style([
  modeBase,
  {
    background: vars.surface.card.hover,
    border: `1px solid ${vars.surface.border.accent}`,
    color: vars.typography.textColor.primary,
    cursor: 'pointer',
  },
]);

/** 안 고른 쪽 — 누를 수 있다(96445435 전에는 예약이 막혀 점선이었다). */
export const modeOff = style([
  modeBase,
  {
    background: 'transparent',
    border: `1px solid ${vars.surface.border.subtle}`,
    color: vars.typography.textColor.secondary,
    cursor: 'pointer',
  },
]);

/** 예약 일시 · 시간대와 30분 잠금 안내. */
export const schedule = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
});

export const scheduleFields = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: vars.spacing.gap.sm,
});

export const scheduleNote = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const modeSub = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const blocked = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.status.warning,
});

export const testNote = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
});

/* ── 받는 사람에게 보이는 모습 (승인 시안 ConfirmAfter 개선안, 2026-09-29) ── */

/** 이번이 몇 회차인가 — 요약 문장 위 한 줄. */
export const sequenceLine = style({
  margin: 0,
  alignSelf: 'flex-end',
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
});

/** 확인 줄 묶음 — 곁 칸이 덮개 밖으로 나가면서 이 면은 한 칸이 됐다(2026-09-29). */
export const stepBody = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.md,
  minWidth: 0,
});

/**
 * 곁 칸의 뿌리.
 *
 * 2열 배치도 오버레이 기준 상자도 덮개(`ScenarioRosterCover` 의 `stepRow`)가 든다. 곁 칸은
 * 단계 상자 **밖**에 형제로 서고(2026-09-29), 전체 미리보기는 그 **두 형제를 다 덮는다** —
 * 🔴 여기에 `position: relative` 를 두면 오른쪽 칸만 덮여 반쪽이 된다(2026-09-29 실측).
 */
export const asideRoot = style({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const recipientHead = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: vars.spacing.gap.sm,
});

/**
 * 받는 사람 화면 — 실제 메일 HTML 을 그대로 그린다.
 *
 * 메일은 흰 바탕이라(`render.ts` 의 `<body>`) 이 어두운 판 위에서 종이 한 장처럼 뜬다. 높이를
 * 고정하고 안에서 스크롤한다 — 본문 길이에 따라 오른쪽 칸이 늘면 왼쪽 확인 줄과 높이가 어긋난다.
 */
export const recipientFrame = style({
  width: '100%',
  height: 360,
  border: `1px solid ${vars.surface.border.subtle}`,
  borderRadius: vars.spacing.borderRadius.md,
});
